import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import cloudinaryService from '../services/cloudinaryService';
import themeService from '../services/themeService';
import { FiHome, FiList, FiTool, FiMessageSquare, FiUser, FiEdit2, FiLogOut, FiHeadphones, FiBookOpen, FiShield, FiUpload, FiStar, FiCheck, FiCamera, FiPhone, FiMapPin, FiMoon, FiSun, FiArrowRight, FiMenu, FiCreditCard, FiHash, FiTrash2, FiX, FiClock } from 'react-icons/fi';
// أضفنا FiTrash2 -----------------------------------^^^^^^^^^^^
import { toast } from 'react-hot-toast';

// دالة مساعدة لتنسيق التقييم
const getFormattedRating = (rating) => {
  if (rating === undefined || rating === null || isNaN(rating)) {
    return "0.0";
  }
  return parseFloat(rating).toFixed(1);
};

const ProviderProfileScreen = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    address: ''
  });

  const [isDarkMode, setIsDarkMode] = useState(themeService.isDarkMode());
  const [deleting, setDeleting] = useState(false); // حالة جديدة للحذف

  // حالات التوثيق المهني
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [docStatus, setDocStatus] = useState(null); // 'pending' | 'approved' | 'rejected' | null
  const [dbDocument, setDbDocument] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);

  // حالات رفع الوثائق الجديدة
  const [newIdFront, setNewIdFront] = useState('');
  const [newIdBack, setNewIdBack] = useState('');
  const [newProfession, setNewProfession] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(null); // 'id_front' | 'id_back' | 'profession'
  const [submittingDocs, setSubmittingDocs] = useState(false);

  useEffect(() => {
    loadProviderData();

    const removeListener = themeService.addListener((theme) => {
      setIsDarkMode(theme === 'dark');
    });

    return () => removeListener();
  }, []);

  const fetchVerificationStatus = async (providerId) => {
    try {
      setLoadingDoc(true);
      const doc = await supabaseService.getProviderDocument(providerId);
      setDbDocument(doc);
      if (doc) {
        setDocStatus(doc.status);
      } else {
        setDocStatus(null);
      }
    } catch (error) {
      console.error("Error fetching verification status:", error);
    } finally {
      setLoadingDoc(false);
    }
  };

  const loadProviderData = async () => {
    try {
      const providerData = await supabaseService.getProviderById(user.id);
      setProvider(providerData);

      if (providerData) {
        setFormData({
          fullName: providerData.fullName || '',
          phone: providerData.phone || '',
          address: providerData.address || ''
        });
        // جلب حالة التوثيق المهني للمزود
        await fetchVerificationStatus(providerData.id);
      }
    } catch (error) {
      console.error("Error loading provider data:", error);
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoadingDoc(false); // تأكيد
      setLoading(false);
    }
  };

  const handleUploadDoc = async (type) => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*,application/pdf';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingDoc(type);
        toast.loading('جاري رفع المستند البنكي/المهني...', { id: 'doc-upload' });

        try {
          const url = await cloudinaryService.uploadImage(file, 'document');
          if (url) {
            if (type === 'id_front') setNewIdFront(url);
            if (type === 'id_back') setNewIdBack(url);
            if (type === 'profession') setNewProfession(url);
            toast.success('تم رفع المستند بنجاح', { id: 'doc-upload' });
          } else {
            toast.error('فشل رفع المستند', { id: 'doc-upload' });
          }
        } catch (error) {
          console.error("Upload document error:", error);
          toast.error('حدث خطأ أثناء الرفع', { id: 'doc-upload' });
        } finally {
          setUploadingDoc(null);
        }
      };
      input.click();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmitVerification = async () => {
    if (!newIdFront || !newIdBack || !newProfession) {
      toast.error('يرجى رفع جميع المستندات المطلوبة');
      return;
    }

    try {
      setSubmittingDocs(true);
      toast.loading('جاري إرسال طلب التوثيق المهني...', { id: 'submit-verification' });

      await supabaseService.createProviderDocument(provider.id, {
        idFrontUrl: newIdFront,
        idBackUrl: newIdBack,
        professionUrl: newProfession
      });

      toast.success('تم إرسال طلب التوثيق بنجاح! الوثائق قيد المراجعة حالياً.', { id: 'submit-verification' });

      // Reset state variables
      setNewIdFront('');
      setNewIdBack('');
      setNewProfession('');

      // Refresh verification status
      await fetchVerificationStatus(provider.id);
    } catch (error) {
      console.error("Submit verification error:", error);
      toast.error('فشل إرسال طلب التوثيق', { id: 'submit-verification' });
    } finally {
      setSubmittingDocs(false);
    }
  };

  const toggleDarkMode = () => {
    const newTheme = themeService.toggleTheme();
    setIsDarkMode(newTheme === 'dark');
    toast.success(`تم تفعيل الوضع ${newTheme === 'dark' ? 'المظلم' : 'الفاتح'}`);
  };

  const updateProfile = async () => {
    try {
      await supabaseService.updateProvider(provider.id, {
        full_name: formData.fullName,
        phone: formData.phone,
        address: formData.address
      });

      setEditingProfile(false);
      loadProviderData();
      toast.success('تم تحديث الملف الشخصي بنجاح');
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error('فشل تحديث الملف الشخصي');
    }
  };

    const handleDeleteAccount = async () => {
    // التأكيد الأول
    const confirm1 = window.confirm('هل أنت متأكد من رغبتك في حذف حسابك؟');
    if (!confirm1) return;

    // التأكيد الثاني (المزيد من التأكيد)
    const confirm2 = window.confirm('تحذير نهائي: سيتم حذف جميع بياناتك وسجلاتك نهائياً ولا يمكن استعادتها. هل تواصل على الحذف؟');
    if (!confirm2) return;

    try {
      setDeleting(true);
      toast.loading('جاري حذف الحساب...', { id: 'delete-account' });

      // استدعاء دالة الحذف من الخدمة
      await supabaseService.deleteProviderAccount(provider.id);

      toast.success('تم حذف الحساب بنجاح', { id: 'delete-account' });

      // التوجيه للصفحة الرئيسية أو تسجيل الدخول
      setTimeout(() => {
        navigate('/');
      }, 1000);

    } catch (error) {
      console.error('Delete error:', error);
      toast.error('حدث خطأ أثناء حذف الحساب', { id: 'delete-account' });
    } finally {
      setDeleting(false);
    }
  };

  const pickAndUploadAvatar = async () => {
    if (uploadingAvatar) return;

    try {
      setUploadingAvatar(true);

      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';

      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (file) {
          const url = await cloudinaryService.uploadImage(file, 'avatar');
          if (url) {
            await supabaseService.updateProvider(provider.id, { avatar_url: url });
            loadProviderData();
            toast.success('تم تحديث الصورة الشخصية');
          }
        }
        setUploadingAvatar(false);
      };

      input.click();
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast.error('فشل رفع الصورة الشخصية');
      setUploadingAvatar(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error('فشل تسجيل الخروج');
    }
  };

    const navigateToPayment = () => {
    navigate('/payment-method');
  };

  // تم حذف الدالة واستبدالها مباشرة في الزر

  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  const tabs = [
    { id: 0, name: 'الرئيسية', icon: FiHome, path: '/provider' },
    { id: 1, name: 'الطلبات', icon: FiList, path: '/provider' },
    { id: 2, name: 'الخدمات', icon: FiTool, path: '/provider' },
    { id: 3, name: 'الدردشة', icon: FiMessageSquare, path: '/provider/conversations' },
    { id: 4, name: 'الملف الشخصي', icon: FiUser, path: '/provider/profile' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 md:h-16 md:w-16 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <>
      {/* تصميم الهاتف */}
      <div className="md:hidden h-screen w-full bg-gray-100 flex items-center justify-center overflow-hidden">
        <div className="max-w-md w-full h-full bg-white flex flex-col relative shadow-2xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-900 to-black p-4 text-white shadow-lg flex items-center justify-between">
            <button
              onClick={() => navigate('/provider')}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <FiArrowRight className="text-xl" />
            </button>
            <h1 className="text-lg font-bold">الملف الشخصي</h1>
            <div className="w-10"></div>
          </div>

          <div className="flex-1 overflow-y-auto pb-20 scroll-smooth">
            <div className="p-4 space-y-4">
              <div className="bg-gradient-to-r from-gray-900 to-black rounded-xl p-6 text-white shadow-lg">
                <div className="flex flex-col items-center">
                  <div className="relative mb-4">
                    <img
                      src={provider?.avatarUrl || 'https://via.placeholder.com/150'}
                      alt="Profile"
                      className="w-24 h-24 rounded-full border-4 border-white"
                    />
                    {provider?.isVerified && (
                      <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full p-1">
                        <FiCheck className="text-white text-xs" />
                      </div>
                    )}
                    <button
                      onClick={pickAndUploadAvatar}
                      className="absolute bottom-0 left-0 bg-white rounded-full p-2 shadow-lg"
                      disabled={uploadingAvatar}
                    >
                      {uploadingAvatar ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                      ) : (
                        <FiCamera className="text-gray-900 text-base" />
                      )}
                    </button>
                  </div>
                  <h2 className="text-xl font-bold mb-2">{provider?.fullName || 'اسم المزود'}</h2>

                  {/* 🆕 Provider ID Display */}
                  <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                    <FiHash className="text-white/70" />
                    <span className="text-white/90 text-sm font-mono">
                      {provider?.providerId || '000000000'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-md border border-gray-200">
                <div className="flex justify-between items-center mb-5">
                  <h3 className="text-lg font-bold">معلوماتي الشخصية</h3>
                  <button
                    onClick={() => setEditingProfile(!editingProfile)}
                    className="text-sm text-gray-600 hover:text-gray-900"
                  >
                    {editingProfile ? 'إلغاء' : 'تعديل'}
                  </button>
                </div>

                {editingProfile ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-700 mb-2 text-sm">الاسم الكامل</label>
                      <input
                        type="text"
                        className="w-full py-2 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 text-sm"
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2 text-sm">رقم الهاتف</label>
                      <input
                        type="tel"
                        className="w-full py-2 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 text-sm"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2 text-sm">العنوان</label>
                      <input
                        type="text"
                        className="w-full py-2 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 text-sm"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      />
                    </div>
                    <button
                      onClick={updateProfile}
                      className="w-full bg-gray-900 text-white py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors text-sm"
                    >
                      حفظ التغييرات
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <div className="bg-gray-900 rounded-full p-2">
                        <FiEdit2 className="text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">البريد الإلكتروني</p>
                        <p className="font-medium text-sm">{provider?.email || ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <div className="bg-gray-900 rounded-full p-2">
                        <FiPhone className="text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">رقم الهاتف</p>
                        {provider?.phone ? (
                          <a href={`tel:${provider.phone}`} className="font-bold text-sm text-blue-600 underline hover:text-blue-800 dir-ltr inline-block">
                            {provider.phone}
                          </a>
                        ) : (
                          <p className="font-medium text-sm text-gray-500">لا يوجد رقم هاتف</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <div className="bg-gray-900 rounded-full p-2">
                        <FiMapPin className="text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">العنوان</p>
                        <p className="font-medium text-sm">{provider?.address || 'لا يوجد عنوان'}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <div className="bg-gray-900 rounded-full p-2">
                        <FiStar className="text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">متوسط التقييم</p>
                        <p className="font-medium text-sm">{getFormattedRating(provider?.averageRating)} / 5.0</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3 space-x-reverse">
                      <div className="bg-gray-900 rounded-full p-2">
                        {isDarkMode ? (
                          <FiMoon className="text-white" />
                        ) : (
                          <FiSun className="text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">الوضع المظلم</p>
                        <div className="flex items-center space-x-3 space-x-reverse">
                          <p className="font-medium text-sm">{isDarkMode ? 'مفعل' : 'غير مفعل'}</p>
                          <button
                            onClick={toggleDarkMode}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                              isDarkMode ? 'bg-gray-800' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                isDarkMode ? 'translate-x-4' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>

                                        {/* 🆕 Professional Card Button */}
                    <button
                      onClick={() => navigate('/member-card')}
                      className="flex items-center space-x-3 space-x-reverse w-full p-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-md"
                    >
                      <div className="bg-white/20 rounded-full p-2">
                        <FiCreditCard className="text-white" />
                      </div>
                      <div className="flex-1 text-right">
                        <p className="font-medium text-sm">بطاقتي المهنية</p>
                        <p className="text-xs text-white/80">Khidma Pro Card & QR Code</p>
                      </div>
                      <FiArrowRight className="text-white rotate-180" />
                    </button>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <button
                  onClick={navigateToPayment}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-200 w-full"
                >
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <div className="bg-gray-900 rounded-full p-2">
                      <FiUpload className="text-white" />
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-sm">الدفع</p>
                      <p className="text-xs text-gray-500">ارفع صورة لإيصال الإيداع الشهري</p>
                    </div>
                  </div>
                  <FiUpload className="text-gray-400" />
                </button>
                <button
                  onClick={() => navigate('/provider/bank-account')}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-200 w-full"
                >
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <div className="bg-gray-900 rounded-full p-2">
                      <FiCreditCard className="text-white" />
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-sm">ربط الحساب البنكي</p>
                      <p className="text-xs text-gray-500">أدخل رقم الـ RIB وصورة الشيك المشطوب</p>
                    </div>
                  </div>
                  <FiCreditCard className="text-gray-400" />
                </button>
                <button
                  onClick={() => setShowVerificationModal(true)}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-200 w-full text-right"
                >
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <div className="bg-gray-900 rounded-full p-2">
                      <FiShield className="text-white" />
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-sm">التوثيق المهني</p>
                      <p className="text-xs text-gray-500">
                        {docStatus === 'approved' && 'حسابك موثق مهنياً'}
                        {docStatus === 'pending' && 'الوثائق تحت المعالجة'}
                        {docStatus === 'rejected' && 'تم رفض الوثائق، أعد الرفع'}
                        {!docStatus && 'ارفع وثائق الهوية وإثبات المهنة'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 space-x-reverse">
                    {docStatus === 'approved' && <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full">موثق</span>}
                    {docStatus === 'pending' && <span className="bg-yellow-100 text-yellow-800 text-[10px] font-bold px-2 py-0.5 rounded-full">تحت المراجعة</span>}
                    {docStatus === 'rejected' && <span className="bg-red-100 text-red-800 text-[10px] font-bold px-2 py-0.5 rounded-full">مرفوض</span>}
                    {!docStatus && <span className="bg-gray-100 text-gray-800 text-[10px] font-bold px-2 py-0.5 rounded-full">غير موثق</span>}
                    <FiShield className="text-gray-400" />
                  </div>
                </button>
                <Link
                  to="/contact-us"
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-200"
                >
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <div className="bg-blue-100 rounded-full p-2">
                      <FiHeadphones className="text-blue-600" />
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-sm">التواصل معنا</p>
                      <p className="text-xs text-gray-500">تواصل مع فريق الدعم</p>
                    </div>
                  </div>
                  <FiHeadphones className="text-gray-400" />
                </Link>
                <Link
                  to="/rules-and-terms"
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-200"
                >
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <div className="bg-orange-100 rounded-full p-2">
                      <FiBookOpen className="text-orange-600" />
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-sm">القواعد والقوانين</p>
                      <p className="text-xs text-gray-500">اطلع على شروط الاستخدام</p>
                    </div>
                  </div>
                  <FiBookOpen className="text-gray-400" />
                </Link>
                <Link
                  to="/privacy-policy"
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-200"
                >
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <div className="bg-emerald-100 rounded-full p-2">
                      <FiShield className="text-emerald-600" />
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-sm">سياسة الخصوصية</p>
                      <p className="text-xs text-gray-500">سياسات الخصوصية وحماية البيانات</p>
                    </div>
                  </div>
                  <FiShield className="text-gray-400" />
                </Link>
                <Link
                  to="/delete-account"
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <div className="bg-red-100 rounded-full p-2">
                      <FiTrash2 className="text-red-600" />
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-sm">حذف الحساب</p>
                      <p className="text-xs text-gray-500">طلب حذف حسابك وبياناتك</p>
                    </div>
                  </div>
                  <FiTrash2 className="text-gray-400" />
                </Link>
              </div>

                            <button
                onClick={handleSignOut}
                className="w-full bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center space-x-2 space-x-reverse text-sm"
              >
                <FiLogOut />
                <span>تسجيل الخروج</span>
              </button>

              {/* 🆕 زر حذف الحساب */}
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="w-full mt-3 border border-red-300 bg-red-50 text-red-700 py-3 rounded-lg font-medium hover:bg-red-100 transition-colors flex items-center justify-center space-x-2 space-x-reverse text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiTrash2 />
                <span>{deleting ? 'جاري الحذف...' : 'حذف الحساب نهائياً'}</span>
              </button>
            </div>
          </div>

          {/* Bottom Navigation */}
          <div className="bg-white border-t border-gray-900 w-full shrink-0 h-16 fixed bottom-0 left-0 right-0 z-50">
            <div className="flex h-full">
              {tabs.map((tab) => {
                const tabPath = tab.id <= 2 ? `/provider?tab=${tab.id}` : tab.path;
                return (
                  <Link
                    key={tab.id}
                    to={tabPath}
                    className={`flex-1 flex flex-col items-center justify-center transition-colors ${
                      isActive(tab.path)
                        ? 'text-gray-900'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <tab.icon className="text-xl mb-1" />
                    <span className="text-xs">{tab.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* تصميم الحاسوب — كل المعلومات ظاهرة بدون تمرير للصفحة، منظّمة في شبكة */}
      <div className="hidden md:flex h-screen bg-gray-50 overflow-hidden">
        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 text-white transition-all duration-300 flex flex-col h-full shrink-0`}>
          <div className="p-3 border-b border-gray-700 flex-shrink-0">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="w-full flex items-center justify-center md:justify-start space-x-2 space-x-reverse hover:bg-gray-800 p-2 rounded-lg transition-colors"
            >
              <FiMenu className="text-xl" />
              {sidebarOpen && <span className="font-medium">القائمة</span>}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-800">
            {tabs.map((tab) => {
              const tabPath = tab.id <= 2 ? `/provider?tab=${tab.id}` : tab.path;
              return (
                <Link
                  key={tab.id}
                  to={tabPath}
                  className={`w-full flex items-center space-x-3 space-x-reverse p-2.5 rounded-lg mb-1 transition-colors ${
                    isActive(tab.path)
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  <tab.icon className="text-lg flex-shrink-0" />
                  {sidebarOpen && <span className="text-sm font-medium">{tab.name}</span>}
                </Link>
              );
            })}
          </div>

          {sidebarOpen && (
            <div className="p-3 border-t border-gray-700 flex-shrink-0 space-y-2">
              <div className="flex items-center space-x-3 space-x-reverse">
                <img
                  src={provider?.avatarUrl || 'https://via.placeholder.com/150'}
                  alt="Profile"
                  className="w-9 h-9 rounded-full"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{provider?.fullName}</p>
                  <p className="text-xs text-gray-400 truncate">{provider?.email}</p>
                </div>
              </div>

              <button
                onClick={handleSignOut}
                className="w-full flex items-center space-x-2 space-x-reverse p-2 rounded-lg hover:bg-red-700 bg-red-600 transition-colors text-sm"
              >
                <FiLogOut className="text-base" />
                <span>تسجيل الخروج</span>
              </button>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden h-full min-w-0">
          <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
            <h1 className="text-xl font-bold text-gray-900">الملف الشخصي</h1>
          </div>

          {/* المحتوى يملأ الارتفاع المتبقي بدون تمرير للصفحة كاملة */}
          <div className="flex-1 min-h-0 px-6 lg:px-8 py-5 lg:py-6">
            <div className="h-full max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6 min-h-0">

              {/* ── العمود الأيمن: الصورة الشخصية + تسجيل الخروج/الحذف ── */}
              <div className="lg:col-span-2 flex flex-col gap-5 min-h-0 overflow-y-auto pr-1">

                <div className="bg-gradient-to-r from-gray-900 to-black rounded-xl p-6 text-white shadow-lg shrink-0">
                  <div className="flex flex-col items-center">
                    <div className="relative mb-4">
                      <img
                        src={provider?.avatarUrl || 'https://via.placeholder.com/150'}
                        alt="Profile"
                        className="w-24 h-24 rounded-full border-4 border-white"
                      />
                      {provider?.isVerified && (
                        <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full p-1">
                          <FiCheck className="text-white text-sm" />
                        </div>
                      )}
                      <button
                        onClick={pickAndUploadAvatar}
                        className="absolute bottom-0 left-0 bg-white rounded-full p-2.5 shadow-lg"
                        disabled={uploadingAvatar}
                      >
                        {uploadingAvatar ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                        ) : (
                          <FiCamera className="text-gray-900 text-base" />
                        )}
                      </button>
                    </div>
                    <h2 className="text-xl font-bold mb-3">{provider?.fullName || 'اسم المزود'}</h2>

                    {/* 🆕 Provider ID Display - Desktop */}
                    <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                      <FiHash className="text-white/70" />
                      <span className="text-white/90 text-sm font-mono">
                        {provider?.providerId || '000000000'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 🆕 Professional Card Button - Desktop */}
                <button
                  onClick={() => navigate('/member-card')}
                  className="flex items-center space-x-3 space-x-reverse w-full p-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg shrink-0"
                >
                  <div className="bg-white/20 rounded-full p-2">
                    <FiCreditCard className="text-white" />
                  </div>
                  <div className="flex-1 text-right">
                    <p className="font-medium text-sm">بطاقتي المهنية</p>
                    <p className="text-xs text-white/80">Khidma Pro Card & QR Code</p>
                  </div>
                  <FiArrowRight className="text-white rotate-180" />
                </button>

                <div className="flex-1"></div>

                <button
                  onClick={handleSignOut}
                  className="w-full bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center space-x-2 space-x-reverse text-sm shrink-0"
                >
                  <FiLogOut />
                  <span>تسجيل الخروج</span>
                </button>

                {/* 🆕 زر حذف الحساب - نسخة سطح المكتب */}
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  className="w-full border border-red-300 bg-red-50 text-red-700 py-3 rounded-lg font-medium hover:bg-red-100 transition-colors flex items-center justify-center space-x-2 space-x-reverse text-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                >
                  <FiTrash2 />
                  <span>{deleting ? 'جاري الحذف...' : 'حذف الحساب نهائياً'}</span>
                </button>
              </div>

              {/* ── العمود الأيسر: معلوماتي الشخصية + القائمة ── */}
              <div className="lg:col-span-3 flex flex-col gap-5 min-h-0 overflow-y-auto pr-1">

                <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 shrink-0">
                  <div className="flex justify-between items-center mb-5">
                    <h3 className="text-lg font-bold">معلوماتي الشخصية</h3>
                    <button
                      onClick={() => setEditingProfile(!editingProfile)}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      {editingProfile ? 'إلغاء' : 'تعديل'}
                    </button>
                  </div>

                  {editingProfile ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-gray-700 mb-2 text-sm">الاسم الكامل</label>
                          <input
                            type="text"
                            className="w-full py-3 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 text-sm"
                            value={formData.fullName}
                            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-gray-700 mb-2 text-sm">رقم الهاتف</label>
                          <input
                            type="tel"
                            className="w-full py-3 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 text-sm"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-2 text-sm">العنوان</label>
                        <input
                          type="text"
                          className="w-full py-3 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 text-sm"
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        />
                      </div>
                      <button
                        onClick={updateProfile}
                        className="w-full bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors text-sm"
                      >
                        حفظ التغييرات
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-3 space-x-reverse">
                        <div className="bg-gray-900 rounded-full p-2 shrink-0">
                          <FiEdit2 className="text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500">البريد الإلكتروني</p>
                          <p className="font-medium text-sm truncate">{provider?.email || ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 space-x-reverse">
                        <div className="bg-gray-900 rounded-full p-2 shrink-0">
                          <FiPhone className="text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500">رقم الهاتف</p>
                          {provider?.phone ? (
                            <a href={`tel:${provider.phone}`} className="font-bold text-sm text-blue-600 underline hover:text-blue-800 dir-ltr inline-block truncate">
                              {provider.phone}
                            </a>
                          ) : (
                            <p className="font-medium text-sm text-gray-500 truncate">لا يوجد رقم هاتف</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 space-x-reverse">
                        <div className="bg-gray-900 rounded-full p-2 shrink-0">
                          <FiMapPin className="text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500">العنوان</p>
                          <p className="font-medium text-sm truncate">{provider?.address || 'لا يوجد عنوان'}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 space-x-reverse">
                        <div className="bg-gray-900 rounded-full p-2 shrink-0">
                          <FiStar className="text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500">متوسط التقييم</p>
                          <p className="font-medium text-sm">{getFormattedRating(provider?.averageRating)} / 5.0</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3 space-x-reverse sm:col-span-2">
                        <div className="bg-gray-900 rounded-full p-2 shrink-0">
                          {isDarkMode ? (
                            <FiMoon className="text-white" />
                          ) : (
                            <FiSun className="text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">الوضع المظلم</p>
                          <div className="flex items-center space-x-3 space-x-reverse">
                            <p className="font-medium text-sm">{isDarkMode ? 'مفعل' : 'غير مفعل'}</p>
                            <button
                              onClick={toggleDarkMode}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                isDarkMode ? 'bg-gray-800' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  isDarkMode ? 'translate-x-5' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden shrink-0">
                  <button
                    onClick={navigateToPayment}
                    className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-200 w-full"
                  >
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <div className="bg-gray-900 rounded-full p-2">
                        <FiUpload className="text-white" />
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-sm">الدفع</p>
                        <p className="text-xs text-gray-500">ارفع صورة لإيصال الإيداع الشهري</p>
                      </div>
                    </div>
                    <FiUpload className="text-gray-400" />
                  </button>
                  <button
                    onClick={() => navigate('/provider/bank-account')}
                    className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-200 w-full"
                  >
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <div className="bg-gray-900 rounded-full p-2">
                        <FiCreditCard className="text-white" />
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-sm">ربط الحساب البنكي</p>
                        <p className="text-xs text-gray-500">أدخل رقم الـ RIB وصورة الشيك المشطوب</p>
                      </div>
                    </div>
                    <FiCreditCard className="text-gray-400" />
                  </button>
                  <button
                    onClick={() => setShowVerificationModal(true)}
                    className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-200 w-full text-right"
                  >
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <div className="bg-gray-900 rounded-full p-2">
                        <FiShield className="text-white" />
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-sm">التوثيق المهني</p>
                        <p className="text-xs text-gray-500">
                          {docStatus === 'approved' && 'حسابك موثق مهنياً'}
                          {docStatus === 'pending' && 'الوثائق تحت المعالجة'}
                          {docStatus === 'rejected' && 'تم رفض الوثائق، أعد الرفع'}
                          {!docStatus && 'ارفع وثائق الهوية وإثبات المهنة'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 space-x-reverse">
                      {docStatus === 'approved' && <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full">موثق</span>}
                      {docStatus === 'pending' && <span className="bg-yellow-100 text-yellow-800 text-[10px] font-bold px-2 py-0.5 rounded-full">تحت المراجعة</span>}
                      {docStatus === 'rejected' && <span className="bg-red-100 text-red-800 text-[10px] font-bold px-2 py-0.5 rounded-full">مرفوض</span>}
                      {!docStatus && <span className="bg-gray-100 text-gray-800 text-[10px] font-bold px-2 py-0.5 rounded-full">غير موثق</span>}
                      <FiShield className="text-gray-400" />
                    </div>
                  </button>
                  <Link
                    to="/contact-us"
                    className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-200"
                  >
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <div className="bg-blue-100 rounded-full p-2">
                        <FiHeadphones className="text-blue-600" />
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-sm">التواصل معنا</p>
                        <p className="text-xs text-gray-500">تواصل مع فريق الدعم</p>
                      </div>
                    </div>
                    <FiHeadphones className="text-gray-400" />
                  </Link>
                  <Link
                    to="/rules-and-terms"
                    className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-200"
                  >
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <div className="bg-orange-100 rounded-full p-2">
                        <FiBookOpen className="text-orange-600" />
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-sm">القواعد والقوانين</p>
                        <p className="text-xs text-gray-500">اطلع على شروط الاستخدام</p>
                      </div>
                    </div>
                    <FiBookOpen className="text-gray-400" />
                  </Link>
                  <Link
                    to="/privacy-policy"
                    className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-200"
                  >
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <div className="bg-emerald-100 rounded-full p-2">
                        <FiShield className="text-emerald-600" />
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-sm">سياسة الخصوصية</p>
                        <p className="text-xs text-gray-500">سياسات الخصوصية وحماية البيانات</p>
                      </div>
                    </div>
                    <FiShield className="text-gray-400" />
                  </Link>
                  <Link
                    to="/delete-account"
                    className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <div className="bg-red-100 rounded-full p-2">
                        <FiTrash2 className="text-red-600" />
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-sm">حذف الحساب</p>
                        <p className="text-xs text-gray-500">طلب حذف حسابك وبياناتك</p>
                      </div>
                    </div>
                    <FiTrash2 className="text-gray-400" />
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ── نافذة التوثيق المهني المنبثقة (Modal) ── */}
      {showVerificationModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm fade-in">
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden slide-up">
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gradient-to-r from-gray-900 to-black text-white shrink-0">
              <div className="flex items-center space-x-2 space-x-reverse">
                <FiShield className="text-xl text-yellow-400" />
                <h3 className="font-bold text-lg">التوثيق المهني للحساب</h3>
              </div>
              <button
                onClick={() => {
                  setShowVerificationModal(false);
                  setNewIdFront('');
                  setNewIdBack('');
                  setNewProfession('');
                }}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors text-white"
              >
                <FiX className="text-xl" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-thin">
              {loadingDoc ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-3">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 dark:border-white"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">جاري تحميل حالة التوثيق...</p>
                </div>
              ) : (
                <>
                  {/* الحالة 1: مقبولة (Approved) */}
                  {docStatus === 'approved' && (
                    <div className="space-y-6">
                      <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-xl p-5 text-center flex flex-col items-center space-y-2">
                        <div className="bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400 rounded-full p-3">
                          <FiCheck className="text-3xl" />
                        </div>
                        <h4 className="font-bold text-lg text-emerald-800 dark:text-emerald-400">حساب موثق مهنياً</h4>
                        <p className="text-sm text-emerald-700 dark:text-emerald-300 max-w-md">
                          تهانينا! لقد تم مراجعة وثائقك والموافقة عليها. حسابك الآن يحمل شارة التوثيق المهني وتظهر خدماتك بشكل مفضل للعملاء.
                        </p>
                      </div>

                      <div className="space-y-3">
                        <h5 className="font-bold text-sm text-gray-700 dark:text-gray-300">الوثائق الموثقة:</h5>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          {dbDocument?.id_front_url && (
                            <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800 p-2">
                              <p className="text-xs font-bold text-center mb-1 text-gray-500">بطاقة الهوية (أمام)</p>
                              <a href={dbDocument.id_front_url} target="_blank" rel="noopener noreferrer" className="block relative aspect-video group">
                                <img src={dbDocument.id_front_url} alt="ID Front" className="w-full h-full object-cover rounded" />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs transition-opacity rounded">عرض المستند</div>
                              </a>
                            </div>
                          )}
                          {dbDocument?.id_back_url && (
                            <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800 p-2">
                              <p className="text-xs font-bold text-center mb-1 text-gray-500">بطاقة الهوية (خلف)</p>
                              <a href={dbDocument.id_back_url} target="_blank" rel="noopener noreferrer" className="block relative aspect-video group">
                                <img src={dbDocument.id_back_url} alt="ID Back" className="w-full h-full object-cover rounded" />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs transition-opacity rounded">عرض المستند</div>
                              </a>
                            </div>
                          )}
                          {dbDocument?.profession_url && (
                            <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800 p-2">
                              <p className="text-xs font-bold text-center mb-1 text-gray-500">وثيقة إثبات المهنة</p>
                              <a href={dbDocument.profession_url} target="_blank" rel="noopener noreferrer" className="block relative aspect-video group">
                                <img src={dbDocument.profession_url} alt="Profession Proof" className="w-full h-full object-cover rounded" />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs transition-opacity rounded">عرض المستند</div>
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* الحالة 2: تحت المعالجة (Pending) */}
                  {docStatus === 'pending' && (
                    <div className="space-y-6">
                      <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-900 rounded-xl p-5 text-center flex flex-col items-center space-y-2">
                        <div className="bg-yellow-100 dark:bg-yellow-900/40 text-yellow-600 dark:text-yellow-400 rounded-full p-3 animate-pulse">
                          <FiClock className="text-3xl" />
                        </div>
                        <h4 className="font-bold text-lg text-yellow-800 dark:text-yellow-400">الوثائق تحت المعالجة</h4>
                        <p className="text-sm text-yellow-700 dark:text-yellow-300 max-w-md">
                          وثائق التوثيق الخاصة بك قيد المراجعة والتحقق حالياً من قبل فريق الإدارة. سيتم تغيير حالة حسابك وتفعيل التوثيق فور التحقق من البيانات.
                        </p>
                      </div>

                      <div className="space-y-3">
                        <h5 className="font-bold text-sm text-gray-700 dark:text-gray-300">الوثائق المرسلة حالياً:</h5>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          {dbDocument?.id_front_url && (
                            <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800 p-2">
                              <p className="text-xs font-bold text-center mb-1 text-gray-500">بطاقة الهوية (أمام)</p>
                              <a href={dbDocument.id_front_url} target="_blank" rel="noopener noreferrer" className="block relative aspect-video">
                                <img src={dbDocument.id_front_url} alt="ID Front" className="w-full h-full object-cover rounded opacity-80" />
                              </a>
                            </div>
                          )}
                          {dbDocument?.id_back_url && (
                            <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800 p-2">
                              <p className="text-xs font-bold text-center mb-1 text-gray-500">بطاقة الهوية (خلف)</p>
                              <a href={dbDocument.id_back_url} target="_blank" rel="noopener noreferrer" className="block relative aspect-video">
                                <img src={dbDocument.id_back_url} alt="ID Back" className="w-full h-full object-cover rounded opacity-80" />
                              </a>
                            </div>
                          )}
                          {dbDocument?.profession_url && (
                            <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800 p-2">
                              <p className="text-xs font-bold text-center mb-1 text-gray-500">وثيقة إثبات المهنة</p>
                              <a href={dbDocument.profession_url} target="_blank" rel="noopener noreferrer" className="block relative aspect-video">
                                <img src={dbDocument.profession_url} alt="Profession Proof" className="w-full h-full object-cover rounded opacity-80" />
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* الحالة 3: مرفوضة (Rejected) أو لم يتم تقديمها بعد */}
                  {(docStatus === 'rejected' || !docStatus) && (
                    <div className="space-y-6">
                      {docStatus === 'rejected' && (
                        <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-xl p-5 text-center flex flex-col items-center space-y-1">
                          <h4 className="font-bold text-lg text-red-800 dark:text-red-400">تم رفض الوثائق السابقة</h4>
                          <p className="text-sm text-red-750 dark:text-red-300">
                            للأسف، لم يتم قبول وثائق التوثيق التي أرسلتها سابقاً. يرجى التأكد من وضوح البيانات وصلاحية الأوراق، ثم أعد رفع الوثائق المطلوبة أدناه.
                          </p>
                        </div>
                      )}

                      {!docStatus && (
                        <div className="bg-blue-50 dark:bg-blue-950/10 border border-blue-200 dark:border-blue-900 rounded-xl p-4">
                          <p className="text-sm text-blue-800 dark:text-blue-300 leading-relaxed text-right">
                            لتوثيق حسابك المهني والحصول على ثقة العملاء الكاملة، يرجى تزويدنا بصور واضحة ومقروءة للمستندات التالية. نقوم بمراجعة الطلبات خلال فترة وجيزة.
                          </p>
                        </div>
                      )}

                      {/* نموذج رفع الوثائق */}
                      <div className="space-y-5">
                        <h4 className="font-bold text-sm text-gray-850 dark:text-gray-200 text-right">الرجاء رفع الوثائق الثلاثة المطلوبة للتوثيق:</h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                          {/* 1. واجهة الهوية */}
                          <div className="flex flex-col items-center">
                            <label className="text-xs font-bold text-gray-650 dark:text-gray-400 mb-2">بطاقة التعريف (الوجه الأمامي) *</label>
                            {newIdFront ? (
                              <div className="relative w-full aspect-video border border-gray-300 dark:border-gray-700 rounded-xl overflow-hidden group">
                                <img src={newIdFront} alt="ID Front Preview" className="w-full h-full object-cover" />
                                <button
                                  onClick={() => setNewIdFront('')}
                                  className="absolute top-2 right-2 bg-red-650 text-white rounded-full p-1.5 hover:bg-red-750 transition-colors shadow-lg"
                                >
                                  <FiTrash2 className="text-xs" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleUploadDoc('id_front')}
                                disabled={uploadingDoc === 'id_front'}
                                className="w-full aspect-video border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl flex flex-col items-center justify-center p-3 hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all text-gray-500 disabled:opacity-50"
                              >
                                {uploadingDoc === 'id_front' ? (
                                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 dark:border-white"></div>
                                ) : (
                                  <>
                                    <FiUpload className="text-2xl mb-1.5" />
                                    <span className="text-xs font-medium text-center">اضغط لرفع واجهة الهوية</span>
                                  </>
                                )}
                              </button>
                            )}
                          </div>

                          {/* 2. خلفية الهوية */}
                          <div className="flex flex-col items-center">
                            <label className="text-xs font-bold text-gray-650 dark:text-gray-400 mb-2">بطاقة التعريف (الوجه الخلفي) *</label>
                            {newIdBack ? (
                              <div className="relative w-full aspect-video border border-gray-300 dark:border-gray-700 rounded-xl overflow-hidden group">
                                <img src={newIdBack} alt="ID Back Preview" className="w-full h-full object-cover" />
                                <button
                                  onClick={() => setNewIdBack('')}
                                  className="absolute top-2 right-2 bg-red-650 text-white rounded-full p-1.5 hover:bg-red-750 transition-colors shadow-lg"
                                >
                                  <FiTrash2 className="text-xs" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleUploadDoc('id_back')}
                                disabled={uploadingDoc === 'id_back'}
                                className="w-full aspect-video border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl flex flex-col items-center justify-center p-3 hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all text-gray-500 disabled:opacity-50"
                              >
                                {uploadingDoc === 'id_back' ? (
                                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 dark:border-white"></div>
                                ) : (
                                  <>
                                    <FiUpload className="text-2xl mb-1.5" />
                                    <span className="text-xs font-medium text-center">اضغط لرفع خلفية الهوية</span>
                                  </>
                                )}
                              </button>
                            )}
                          </div>

                          {/* 3. إثبات المهنة */}
                          <div className="flex flex-col items-center">
                            <label className="text-xs font-bold text-gray-655 dark:text-gray-400 mb-2">وثيقة إثبات المهنة *</label>
                            {newProfession ? (
                              <div className="relative w-full aspect-video border border-gray-300 dark:border-gray-700 rounded-xl overflow-hidden group">
                                <img src={newProfession} alt="Profession Preview" className="w-full h-full object-cover" />
                                <button
                                  onClick={() => setNewProfession('')}
                                  className="absolute top-2 right-2 bg-red-655 text-white rounded-full p-1.5 hover:bg-red-750 transition-colors shadow-lg"
                                >
                                  <FiTrash2 className="text-xs" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleUploadDoc('profession')}
                                disabled={uploadingDoc === 'profession'}
                                className="w-full aspect-video border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl flex flex-col items-center justify-center p-3 hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all text-gray-500 disabled:opacity-50"
                              >
                                {uploadingDoc === 'profession' ? (
                                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 dark:border-white"></div>
                                ) : (
                                  <>
                                    <FiUpload className="text-2xl mb-1.5" />
                                    <span className="text-xs font-medium text-center">اضغط لرفع إثبات المهنة</span>
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* زر التقديم */}
                        <div className="pt-4 border-t border-gray-150 dark:border-gray-850">
                          <button
                            onClick={handleSubmitVerification}
                            disabled={!newIdFront || !newIdBack || !newProfession || submittingDocs}
                            className="w-full py-3 bg-gradient-to-r from-gray-900 to-black hover:from-black hover:to-gray-900 text-white rounded-xl font-bold shadow-lg transition-all flex items-center justify-center space-x-2 space-x-reverse disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {submittingDocs ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            ) : (
                              <>
                                <FiShield />
                                <span>إرسال طلب التوثيق المهني</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProviderProfileScreen;